/**
 * Staff page: the external testers who sign in with email + password.
 *
 * Staff (Entra) create an account here and hand the tester the temporary
 * password it produces; the tester is forced to replace it on first login.
 * Everything below talks to /api/auth/local/users, which the backend restricts
 * to Entra-signed-in principals (a tester's own token gets 403), so the page
 * also refuses to render its controls for a local session — see StaffOnly.
 *
 * Layout follows the Devices page: a summary strip, a section header, one
 * card per tester with eyebrow-labelled facts and the row's actions on the
 * right. Delete is a two-click action like "Stop training" on Model Updates.
 */
import { useCallback, useEffect, useState, type CSSProperties, type FormEvent } from "react";
import { useMsal } from "@azure/msal-react";
import { apiScopes } from "../auth/msalConfig";
import { ApiError, isLocalAuth, localAuthApi, type TesterRecord } from "../auth/localAuth";
import { theme } from "../theme";

// ── Shared styles (same idiom as DevicesPage) ─────────────────────────────────

const EYEBROW: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: "0.12em",
  textTransform: "uppercase",
  color: theme.textMuted,
};

const INPUT: CSSProperties = {
  padding: "8px 10px", fontSize: theme.fontSizeXs, fontWeight: 600,
  fontFamily: theme.fontSans, borderRadius: 5,
  border: `1px solid ${theme.borderStrong}`,
  background: theme.bgCard, color: theme.textPrimary, outline: "none",
  minWidth: 0,
};

const PRIMARY_BTN: CSSProperties = {
  padding: "8px 14px", fontSize: theme.fontSizeXs, fontWeight: 700, borderRadius: 5,
  border: "none", background: theme.gold, color: theme.onGold, cursor: "pointer",
  fontFamily: "inherit", whiteSpace: "nowrap",
};

const SECONDARY_BTN: CSSProperties = {
  padding: "7px 12px", fontSize: theme.fontSizeXs, fontWeight: 700, borderRadius: 5,
  border: `1px solid ${theme.border}`, background: theme.bgCard, color: theme.textSecondary,
  cursor: "pointer", fontFamily: "inherit", whiteSpace: "nowrap",
};

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof ApiError ? err.message : fallback;
}

// ── Small pieces ──────────────────────────────────────────────────────────────

function SectionHeader({ title, hint, count }: { title: string; hint?: string; count?: string }) {
  return (
    <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
      <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: theme.textPrimary, letterSpacing: "0.01em" }}>
        {title}
      </h2>
      {count && <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>{count}</span>}
      {hint && <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>— {hint}</span>}
    </div>
  );
}

/** Active/inactive as a pill; inactive is the resting grey, not an alarm. */
function ActivePill({ active }: { active: boolean }) {
  const color = active ? theme.success : theme.textMuted;
  return (
    <span
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        fontSize: 11, fontWeight: 700, letterSpacing: "0.04em",
        padding: "3px 10px", borderRadius: 11,
        color, background: active ? theme.successSoft : "transparent",
        border: `1px solid ${active ? theme.success : theme.border}`,
        textTransform: "uppercase", whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: color, display: "inline-block" }} />
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function Fact({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={EYEBROW}>{label}</div>
      <div style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary, marginTop: 2, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {children}
      </div>
    </div>
  );
}

function ErrorLine({ text }: { text: string }) {
  return (
    <div role="alert" style={{
      color: theme.danger, background: theme.dangerSoft, border: `1px solid ${theme.danger}`,
      borderRadius: 5, padding: "8px 10px", fontSize: theme.fontSize2Xs, lineHeight: 1.4,
    }}>
      {text}
    </div>
  );
}

/** The one place a temporary password is ever visible. Shown once, right after
 *  it is generated; the backend only stores a hash, so a reload loses it. */
function TemporaryPasswordBox({ email, password, onDismiss }: { email: string; password: string; onDismiss: () => void }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      setCopied(true);
    } catch {
      // Clipboard blocked (insecure context / permissions): the field below is
      // selectable, so the user can still copy by hand.
    }
  };

  return (
    <div style={{
      border: `1px solid ${theme.warning}`, background: theme.warningSoft, borderRadius: theme.radius,
      padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
        <span style={{ ...EYEBROW, color: theme.warning }}>Temporary password</span>
        <span style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary }}>for {email}</span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <input
          readOnly
          value={password}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            ...INPUT, flex: 1, minWidth: 180, fontFamily: theme.fontMono, fontSize: theme.fontSizeSm,
            letterSpacing: "0.04em", background: theme.bgCard,
          }}
        />
        <button type="button" onClick={copy} style={{ ...PRIMARY_BTN, minWidth: 74 }}>
          {copied ? "Copied" : "Copy"}
        </button>
        <button type="button" onClick={onDismiss} style={SECONDARY_BTN}>Done</button>
      </div>
      <div style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary, lineHeight: 1.45 }}>
        Share this with the tester now — it will not be shown again. They will be asked to choose their own password at first sign-in.
      </div>
    </div>
  );
}

// ── Add tester ────────────────────────────────────────────────────────────────

function AddTesterForm({ onCreate }: { onCreate: (email: string, name: string) => Promise<void> }) {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const canSubmit = !busy && email.trim().length > 0 && name.trim().length > 0;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onCreate(email.trim(), name.trim());
      setEmail("");
      setName("");
    } catch (err) {
      setError(errorText(err, "Could not create the tester — please try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      style={{
        background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: theme.radius,
        padding: "14px 18px", display: "flex", flexDirection: "column", gap: 10,
      }}
    >
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 220px" }}>
          <label htmlFor="tester-email" style={EYEBROW}>Email</label>
          <input
            id="tester-email" type="email" required autoComplete="off" placeholder="tester@example.com"
            value={email} disabled={busy} onChange={(e) => { setEmail(e.target.value); setError(null); }}
            style={INPUT}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 5, flex: "1 1 180px" }}>
          <label htmlFor="tester-name" style={EYEBROW}>Name</label>
          <input
            id="tester-name" type="text" required autoComplete="off" placeholder="Full name"
            value={name} disabled={busy} onChange={(e) => { setName(e.target.value); setError(null); }}
            style={INPUT}
          />
        </div>
        <button
          type="submit" disabled={!canSubmit}
          style={{ ...PRIMARY_BTN, cursor: canSubmit ? "pointer" : "default", opacity: canSubmit ? 1 : 0.6 }}
        >
          {busy ? "Adding…" : "Add tester"}
        </button>
      </div>
      {error && <ErrorLine text={error} />}
    </form>
  );
}

// ── One tester ────────────────────────────────────────────────────────────────

function TesterRow({ t, token, onChanged, onDeleted }: {
  t: TesterRecord;
  token: () => Promise<string>;
  onChanged: (updated: TesterRecord) => void;
  onDeleted: (id: TesterRecord["id"]) => void;
}) {
  const [busy, setBusy] = useState<"reset" | "toggle" | "delete" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Delete is a two-click action, same as "Stop training" on Model Updates:
  // the first click arms "Confirm delete", which disarms by itself after a few
  // seconds. (No window.confirm — browser dialogs are not used in this app.)
  const [deleteArmed, setDeleteArmed] = useState(false);
  useEffect(() => {
    if (!deleteArmed) return;
    const timer = window.setTimeout(() => setDeleteArmed(false), 4000);
    return () => window.clearTimeout(timer);
  }, [deleteArmed]);

  const lockedUntil = t.locked_until ? new Date(t.locked_until) : null;
  const isLocked = lockedUntil !== null && !Number.isNaN(lockedUntil.getTime()) && lockedUntil.getTime() > Date.now();

  const run = async (kind: "reset" | "toggle" | "delete", fn: () => Promise<void>, fallback: string) => {
    setBusy(kind);
    setError(null);
    try {
      await fn();
    } catch (err) {
      setError(errorText(err, fallback));
    } finally {
      setBusy(null);
    }
  };

  const reset = () => run("reset", async () => {
    const r = await localAuthApi.resetTesterPassword(await token(), t.id);
    setTempPassword(r.temporary_password);
    onChanged({ ...t, must_change_password: true });
  }, "Could not reset the password.");

  const toggle = () => run("toggle", async () => {
    const updated = await localAuthApi.patchTester(await token(), t.id, { is_active: !t.is_active });
    // Some backends answer a PATCH with the row, some with 204; either way the
    // state we asked for is the state we show.
    onChanged({ ...t, ...(updated ?? {}), is_active: !t.is_active });
  }, "Could not update the tester.");

  const remove = () => {
    if (!deleteArmed) { setDeleteArmed(true); return; }
    setDeleteArmed(false);
    void run("delete", async () => {
      await localAuthApi.deleteTester(await token(), t.id);
      onDeleted(t.id);
    }, "Could not delete the tester.");
  };

  return (
    <div
      style={{
        background: theme.bgCard,
        border: `1px solid ${theme.border}`,
        borderRadius: theme.radius,
        overflow: "hidden",
        opacity: t.is_active ? 1 : 0.82,
      }}
    >
      {/* Header: identity + state — wraps at narrow widths like the device rows. */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 18px", flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 180 }}>
          <div style={{
            fontFamily: theme.fontMono, fontSize: theme.fontSizeSm, fontWeight: 700,
            color: theme.textPrimary, letterSpacing: "0.03em",
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
          }}>
            {t.email}
          </div>
          <div style={{ fontSize: theme.fontSizeXs, color: theme.textSecondary, marginTop: 2 }}>{t.name}</div>
        </div>

        <ActivePill active={t.is_active} />
        {t.must_change_password && (
          <span title="Signed in with a temporary password not yet replaced" style={{
            fontSize: 11, fontWeight: 700, letterSpacing: "0.04em", padding: "3px 10px", borderRadius: 11,
            color: theme.goldText, background: theme.goldSoft, border: `1px solid ${theme.gold}`,
            textTransform: "uppercase", whiteSpace: "nowrap",
          }}>
            Temp password
          </span>
        )}

        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={EYEBROW}>Last login</div>
          <div style={{ fontSize: theme.fontSize2Xs, color: theme.textSecondary, marginTop: 2 }}>{fmtDateTime(t.last_login_at)}</div>
        </div>
      </div>

      {isLocked && (
        <div style={{
          margin: "0 18px", padding: "8px 10px", borderRadius: 5,
          background: theme.warningSoft, border: `1px solid ${theme.warning}`,
          color: theme.warning, fontSize: theme.fontSize2Xs, fontWeight: 600,
        }}>
          Locked after too many failed sign-in attempts — until {fmtDateTime(t.locked_until)}
        </div>
      )}

      {/* Footer: facts + actions */}
      <div style={{
        display: "flex", alignItems: "center", gap: 18, padding: "12px 18px 14px", flexWrap: "wrap",
        borderTop: `1px solid ${theme.border}`, marginTop: 12,
      }}>
        <Fact label="Created">{fmtDateTime(t.created_at)}</Fact>
        <Fact label="Created by">{t.created_by || "—"}</Fact>

        <div style={{ marginLeft: "auto", display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button
            type="button" disabled={busy !== null} onClick={reset}
            title="Generate a new temporary password; the tester must change it at next sign-in"
            style={{ ...SECONDARY_BTN, cursor: busy ? "default" : "pointer", opacity: busy && busy !== "reset" ? 0.6 : 1 }}
          >
            {busy === "reset" ? "Resetting…" : "Reset password"}
          </button>
          <button
            type="button" disabled={busy !== null} onClick={toggle}
            title={t.is_active ? "Block sign-in and invalidate the tester's current session" : "Allow this tester to sign in again"}
            style={{ ...SECONDARY_BTN, cursor: busy ? "default" : "pointer", opacity: busy && busy !== "toggle" ? 0.6 : 1 }}
          >
            {busy === "toggle" ? "Saving…" : t.is_active ? "Deactivate" : "Activate"}
          </button>
          <button
            type="button" disabled={busy !== null} onClick={remove}
            title={deleteArmed ? "Click again to delete this tester" : "Delete this tester account"}
            style={{
              ...SECONDARY_BTN,
              background: deleteArmed ? theme.dangerSoft : theme.bgCard, color: theme.danger,
              border: `1px solid ${theme.danger}`,
              cursor: busy ? "default" : "pointer", opacity: busy && busy !== "delete" ? 0.6 : 1,
            }}
          >
            {busy === "delete" ? "Deleting…" : deleteArmed ? "Confirm delete" : "Delete"}
          </button>
        </div>
      </div>

      {(tempPassword || error) && (
        <div style={{ padding: "0 18px 14px", display: "flex", flexDirection: "column", gap: 8 }}>
          {tempPassword && (
            <TemporaryPasswordBox email={t.email} password={tempPassword} onDismiss={() => setTempPassword(null)} />
          )}
          {error && <ErrorLine text={error} />}
        </div>
      )}
    </div>
  );
}

// ── Route guard ───────────────────────────────────────────────────────────────

function StaffOnly() {
  return (
    <div style={{ padding: 28, maxWidth: 980 }}>
      <div style={{
        background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: theme.radius,
        padding: "28px 24px", maxWidth: 460, display: "flex", flexDirection: "column", gap: 6,
      }}>
        <span style={EYEBROW}>Staff only</span>
        <span style={{ fontSize: theme.fontSizeSm, fontWeight: 700, color: theme.textPrimary }}>
          This page is for Intelliport staff
        </span>
        <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted, lineHeight: 1.45 }}>
          Tester accounts are managed by staff signed in with Microsoft. Your account cannot view or change them.
        </span>
      </div>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function TestersPage() {
  // Decided at render, not at module load: the same bundle serves both kinds
  // of principal and a tester could type the URL by hand.
  if (isLocalAuth()) return <StaffOnly />;
  return <TestersAdmin />;
}

function TestersAdmin() {
  const { instance, accounts } = useMsal();
  const [testers, setTesters] = useState<TesterRecord[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [created, setCreated] = useState<{ email: string; password: string } | null>(null);

  // Whatever the MSAL instance hands out: an Entra token for staff, the dev
  // token in loop-engineering mode. The backend decides who counts as staff.
  const token = useCallback(async () => {
    const r = await instance.acquireTokenSilent({ scopes: apiScopes, account: accounts[0] });
    return r.accessToken;
  }, [instance, accounts]);

  const load = useCallback(async () => {
    try {
      const rows = await localAuthApi.listTesters(await token());
      setTesters(rows);
      setLoadError(null);
    } catch (err) {
      setTesters((prev) => prev ?? []);
      setLoadError(err instanceof ApiError && err.status === 403
        ? "Your account is not allowed to manage testers."
        : errorText(err, "Could not load the tester list."));
    }
  }, [token]);

  useEffect(() => { void load(); }, [load]);

  const handleCreate = async (email: string, name: string) => {
    const r = await localAuthApi.createTester(await token(), email, name);
    setTesters((prev) => [r.user, ...(prev ?? []).filter((t) => t.id !== r.user.id)]);
    setCreated({ email: r.user.email, password: r.temporary_password });
  };

  const handleChanged = (updated: TesterRecord) =>
    setTesters((prev) => (prev ?? []).map((t) => (t.id === updated.id ? updated : t)));

  const handleDeleted = (id: TesterRecord["id"]) =>
    setTesters((prev) => (prev ?? []).filter((t) => t.id !== id));

  const activeCount = (testers ?? []).filter((t) => t.is_active).length;

  return (
    <div style={{ padding: 28, maxWidth: 980 }}>
      {/* No page title, matching the other pages: the rail names the view. */}

      {/* ── Add tester ─────────────────────────────────────────────── */}
      <section style={{ marginBottom: 28 }}>
        <SectionHeader
          title="Add tester"
          hint="creates an email + password account and shows its temporary password once"
        />
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <AddTesterForm onCreate={handleCreate} />
          {created && (
            <TemporaryPasswordBox email={created.email} password={created.password} onDismiss={() => setCreated(null)} />
          )}
        </div>
      </section>

      {/* ── Testers ────────────────────────────────────────────────── */}
      <section style={{ borderTop: `1px solid ${theme.border}`, paddingTop: 22 }}>
        <SectionHeader
          title="Testers"
          hint="external accounts that sign in with email and password"
          count={testers && testers.length > 0
            ? `${testers.length} account${testers.length !== 1 ? "s" : ""} · ${activeCount} active`
            : undefined}
        />

        {loadError && <div style={{ marginBottom: 12 }}><ErrorLine text={loadError} /></div>}

        {testers === null ? (
          <div style={{ color: theme.textMuted, fontSize: theme.fontSizeXs }}>Loading…</div>
        ) : testers.length === 0 ? (
          <div
            style={{
              border: `1px dashed ${theme.border}`, borderRadius: theme.radius, background: theme.bgCard,
              padding: "44px 24px", display: "flex", flexDirection: "column", alignItems: "center",
              gap: 10, textAlign: "center", color: theme.textMuted,
            }}
          >
            <span style={{ fontSize: 32, color: theme.textMuted }}>⊘</span>
            <span style={{ fontSize: theme.fontSizeSm, fontWeight: 700, color: theme.textSecondary }}>
              No testers yet
            </span>
            <span style={{ fontSize: theme.fontSize2Xs, color: theme.textMuted }}>
              Add one above to give an external tester access with email and password.
            </span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {testers.map((t) => (
              <TesterRow key={String(t.id)} t={t} token={token} onChanged={handleChanged} onDeleted={handleDeleted} />
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
